import React, { useState, useContext, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import toast from "react-hot-toast";
import { ArrowLeft, Phone, Calendar, Trash2, ChevronDown, MessageSquare, X } from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Switch } from "../../ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import Text from "../../Tags/Text";
import SharedDataContext from "../../../SharedDataContext";
import {
  deletePhoneNumber,
  updatePhoneNumber,
  getTelephonyUsersSimple,
  getAllPhoneNumbers,
  getAccessiblePhoneNumbers,
  getPhoneNumberById,
} from "../../../apis/phoneInventory";
import VirtualTelephonyUserSelect from "../shared/VirtualTelephonyUserSelect";
import { getCountryFlag } from "../../../constants/countries";
import useModal from "../../GenericModal/useModal";
import GenericModal from "../../GenericModal";
import Flag from "../../Flag";
import PageHeader from "../../PageHeader";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";
import PrintDate from "../../Tags/PrintDate";

export default function PhoneNumberSettingsContainer() {
  const { isOpen, openModal, closeModal } = useModal(false);
  const { phoneId, workspaceSlug } = useParams();
  const navigate = useNavigate();
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};
  const captureEvent = useCaptureEvent();

  const [phoneSettings, setPhoneSettings] = useState({
    label: "",
    accessType: "workspace",
    assignedUsers: [],
    recordingEnabled: true,
    callForwarding: {
      enabled: false,
      number: "",
    },
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formInitializedFor, setFormInitializedFor] = useState(null);

  const isAdmin =
    activeWorkspace?.role === "Owner" ||
    activeWorkspace?.role === "Admin" ||
    activeWorkspace?.role === "Super Admin";

  const fetchPhoneNumbersFn = isAdmin ? getAllPhoneNumbers : getAccessiblePhoneNumbers;

  const {
    data: phoneNumbersResponse,
    isLoading: isLoadingPhoneNumbers,
    isError: isErrorPhoneNumbers,
  } = useQuery(
    ["phone_number_detail", activeWorkspace?.id, phoneId, isAdmin ? "admin" : "user"],
    () =>
      getPhoneNumberById({
        phoneNumberId: phoneId,
        options: {
          headers: { "X-Workspace-Id": activeWorkspace?.id },
        },
      }),
    {
      enabled: !!activeWorkspace?.id && !!phoneId,
      retry: 2,
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error("Error fetching phone numbers:", error);
        toast.error("Failed to load phone numbers. Please refresh the page.");
      },
    }
  );

  const phoneNumbersList = useMemo(() => {
    if (phoneNumbersResponse?.data) {
      return [phoneNumbersResponse.data];
    }
    if (phoneNumbersResponse && !phoneNumbersResponse.data) {
      return [phoneNumbersResponse];
    }
    return [];
  }, [phoneNumbersResponse]);

  const phoneNumberData = useMemo(() => {
    const targetId = phoneId?.toString();
    return phoneNumbersList.find((number) => number?.id?.toString() === targetId) || null;
  }, [phoneNumbersList, phoneId]);

  useEffect(() => {
    setFormInitializedFor(null);
  }, [phoneId]);

  useEffect(() => {
    if (!phoneNumberData) return;

    const phoneKey = phoneNumberData.id?.toString() || phoneId?.toString();
    if (!phoneKey) return;

    const assignedUserIds = Array.isArray(phoneNumberData.assigned_users)
      ? phoneNumberData.assigned_users.map((user) => user.id).filter(Boolean)
      : [];

    const computedAccessType =
      (phoneNumberData.type || "").toLowerCase() === "assigned" || assignedUserIds.length > 0
        ? "assigned"
        : "workspace";

    if (formInitializedFor !== phoneKey) {
      setPhoneSettings({
        label: phoneNumberData.label || "",
        accessType: computedAccessType,
        assignedUsers: assignedUserIds,
        recordingEnabled: phoneNumberData.recording_enabled ?? true,
        // callForwarding: {
        //   enabled: !!phoneNumberData.call_forwarding?.enabled,
        //   number: phoneNumberData.call_forwarding?.number || "",
        // },
      });

      setFormInitializedFor(phoneKey);
      return;
    }

    setPhoneSettings((prev) => {
      const prevAssigned = Array.isArray(prev.assignedUsers) ? prev.assignedUsers : [];

      if (prevAssigned.length === assignedUserIds.length) {
        const prevAssignedSet = new Set(prevAssigned);
        const isSame = assignedUserIds.every((id) => prevAssignedSet.has(id));
        if (isSame) {
          return prev;
        }
      }

      return { ...prev, assignedUsers: assignedUserIds };
    });
  }, [phoneNumberData, phoneId, formInitializedFor]);

  useEffect(() => {
    if (phoneId && activeWorkspace?.id) {
      captureEvent("SET_PhoneNumberSettings_View", {
        phone_number_id: phoneId,
        workspace_id: activeWorkspace.id,
      });
    }
  }, [phoneId, activeWorkspace?.id, captureEvent]);

  const {
    data: telephonyUsersResponse,
    isLoading: isLoadingUsers,
    isError: isErrorUsers,
  } = useQuery(
    ["get_telephony_users", activeWorkspace?.id],
    () =>
      getTelephonyUsersSimple({
        options: {
          headers: { "X-Workspace-Id": activeWorkspace?.id },
        },
      }),
    {
      enabled: !!activeWorkspace?.id,
      retry: 2,
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error("❌ Error fetching telephony users:", error);
        toast.error("Failed to load available users. Please refresh the page.");
      },
    }
  );

  const availableUsers =
    telephonyUsersResponse?.data?.map((user) => ({
      id: user.id,
      name: user.name,
      email: user.email,
    })) || [];

  const assignedUsersFromNumber = phoneNumberData?.assigned_users || [];

  const selectableUsers = useMemo(() => {
    const combinedUsersMap = new Map();

    [...availableUsers, ...assignedUsersFromNumber].forEach((user) => {
      if (user && user.id) {
        combinedUsersMap.set(user.id, user);
      }
    });

    phoneSettings.assignedUsers.forEach((userId) => {
      if (userId && !combinedUsersMap.has(userId)) {
        combinedUsersMap.set(userId, { id: userId, name: userId });
      }
    });

    return Array.from(combinedUsersMap.values());
  }, [availableUsers, assignedUsersFromNumber, phoneSettings.assignedUsers]);

  const handleClose = () => {
    navigate(`/${workspaceSlug}/numbers-inventory`);
  };

  const updatePhoneNumberMutation = useMutation(updatePhoneNumber, {
    onSuccess: () => {
      toast.success("Phone number settings saved successfully!");
      navigate(`/${workspaceSlug}/numbers-inventory`);
      setIsSaving(false);
      captureEvent("SET_PhoneNumberSettings_SaveSuccess", {
        phone_number_id: phoneNumberData?.id || phoneId,
      });
    },
    onError: (error) => {
      console.error("Error updating phone number:", error);
      toast.error("Failed to save phone number settings. Please try again.");
      setIsSaving(false);
      captureEvent("SET_PhoneNumberSettings_SaveFailure", {
        phone_number_id: phoneNumberData?.id || phoneId,
        error: error?.message || "unknown_error",
      });
    },
  });

  const handleSaveChanges = () => {
    if (!phoneNumberData) {
      toast.error("Unable to save. Phone number not found.");
      return;
    }

    setIsSaving(true);

    const updateData = {
      label: phoneSettings.label,
      type: phoneSettings.accessType === "assigned" ? "Assigned" : "Shared",
      assign_to_users:
        phoneSettings.accessType === "assigned" ? phoneSettings.assignedUsers.filter(Boolean) : [],
      // call_forwarding: {
      //   enabled: phoneSettings.callForwarding.enabled,
      //   number: phoneSettings.callForwarding.number || null,
      // },
      recording_enabled: phoneSettings.recordingEnabled,
    };

    captureEvent("SET_PhoneNumberSettings_SaveClick", {
      phone_number_id: phoneNumberData?.id || phoneId,
      access_type: phoneSettings.accessType,
      assigned_user_count: phoneSettings.assignedUsers?.length || 0,
      recording_enabled: phoneSettings.recordingEnabled,
    });

    updatePhoneNumberMutation.mutate({
      phoneNumberId: phoneNumberData.id || phoneId,
      data: updateData,
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  const deletePhoneNumberMutation = useMutation(deletePhoneNumber, {
    onSuccess: () => {
      toast.success("Phone number deleted successfully!");
      setIsDeleting(false);
      navigate(`/${workspaceSlug}/numbers-inventory`);
      captureEvent("SET_PhoneNumberSettings_DeleteSuccess", {
        phone_number_id: phoneNumberData?.id || phoneId,
      });
    },
    onError: (error) => {
      console.error("Error deleting phone number:", error);
      toast.error("Failed to delete phone number. Please try again.");
      setIsDeleting(false);
      captureEvent("SET_PhoneNumberSettings_DeleteFailure", {
        phone_number_id: phoneNumberData?.id || phoneId,
        error: error?.message || "unknown_error",
      });
    },
  });

  const handleDeleteNumber = () => {
    if (!phoneNumberData) {
      toast.error("Unable to delete. Phone number not found.");
      return;
    }

    setIsDeleting(true);

    captureEvent("SET_PhoneNumberSettings_DeleteClick", {
      phone_number_id: phoneNumberData?.id || phoneId,
    });

    deletePhoneNumberMutation.mutate({
      phoneNumberId: phoneNumberData.id || phoneId,
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  const getAssignedUsers = () => {
    if (
      phoneSettings &&
      phoneSettings.assignedUsers &&
      phoneSettings.assignedUsers.length > 0 &&
      selectableUsers &&
      selectableUsers.length > 0
    ) {
      const idSet = new Set(phoneSettings.assignedUsers);
      const matched = selectableUsers.filter((u) => idSet.has(u.id));

      console.log(matched);
      return matched || [];
    } else {
      return [];
    }
  };

  const getInitials = (name = "") => {
    if (!name) return "";

    const words = name.trim().split(/\s+/); // split by spaces
    if (words.length === 1) {
      return words[0][0].toUpperCase();
    }

    return (words[0][0] + words[1][0]).toUpperCase();
  };

  if (isLoadingPhoneNumbers) {
    return (
      <div
        className="bg-gray-25 dark:bg-gray-dark-50 -mx-8 -my-8 px-4 sm:px-8 lg:px-[60px] py-8 border-t border-border flex justify-center items-center"
        style={{ minHeight: "calc(100vh - 72px)" }}
      >
        <div className="flex h-full items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-[#475467]">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#D9D6FE] border-t-[#7F56D9]"></div>
            Loading phone number details...
          </div>
        </div>
      </div>
    );
  }

  if (isErrorPhoneNumbers) {
    return (
      <div
        className="bg-gray-25 dark:bg-gray-dark-50 -mx-8 -my-8 px-4 sm:px-8 lg:px-[60px] py-8 border-t border-border"
        style={{ minHeight: "calc(100vh - 72px)" }}
      >
        <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
          <Text size="lg" className="font-semibold text-[#101828]">
            Something went wrong
          </Text>
          <p className="text-sm text-[#475467] max-w-sm">
            We couldn’t load the phone number details. Please try again later or refresh the page.
          </p>
          <Button onClick={handleClose} variant="outline">
            Return to numbers inventory
          </Button>
        </div>
      </div>
    );
  }

  if (!phoneNumberData) {
    return (
      <div
        className="bg-gray-25 dark:bg-gray-dark-50 -mx-8 -my-8 px-4 sm:px-8 lg:px-[60px] py-8 border-t border-border"
        style={{ minHeight: "calc(100vh - 72px)" }}
      >
        <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
          <Text size="lg" className="font-semibold text-[#101828]">
            Phone number not found
          </Text>
          <p className="text-sm text-[#475467] max-w-sm">
            We couldn’t find the phone number you’re trying to view. It may have been removed or you may not
            have access to it.
          </p>
          <Button onClick={handleClose} variant="outline">
            Return to numbers inventory
          </Button>
        </div>
      </div>
    );
  }

  const isSettingsReady = formInitializedFor === (phoneNumberData.id?.toString() || phoneId?.toString());

  return (
    <>
      <div style={{ minHeight: "calc(100vh - 72px)" }}>
        <div className="w-full  mx-auto">
          {/* Header */}

          <PageHeader goBack={true}>
            <div>
              {/* Close Button - Top Row */}
              <div className="mb-2">
                <span onClick={handleClose} className="cursor-pointer inline-flex items-center">
                  <ArrowLeft className="h-4 w-4 text-gray-500 " />
                  <span className="ml-1 text-sm text-gray-500 font-semibold">Close</span>
                </span>
              </div>

              {/* Phone Number Info and Actions - Second Row */}
              <div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-[50px] rounded-full overflow-hidden border border-border leading-none">
                      <Flag code={phoneNumberData?.country} squared />
                    </span>
                    <div>
                      <div className="flex items-center space-x-2">
                        <Text size="xl" className="font-semibold text-gray-950">
                          {phoneNumberData.phone_number}
                        </Text>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-600 mt-1">
                        <span>{phoneNumberData.label || "Unlabeled number"}</span>
                        <MessageSquare className="h-4 w-4" />
                        <Phone className="h-4 w-4" />
                        <Calendar className="h-4 w-4" />
                        <span><PrintDate date={phoneNumberData.created_at} /></span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="flex items-center space-x-1">
                          <span>Manage</span>
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => {
                            captureEvent("SET_PhoneNumberSettings_DeleteModalOpen", {
                              phone_number_id: phoneNumberData?.id || phoneId,
                              assigned_users_count: getAssignedUsers().length,
                            });
                            openModal();
                          }}
                          disabled={isDeleting}
                          className="text-red-600 hover:text-red-700 disabled:text-red-400 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {isDeleting ? "Deleting..." : "Delete number"}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button
                      onClick={handleSaveChanges}
                      disabled={isDeleting || isSaving || !isSettingsReady}
                      className="bg-[#7F56D9] hover:bg-[#6D47C7] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                </div>

                {!isSettingsReady && (
                  <div className="mt-4 flex items-center gap-2 text-sm text-gray-950">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#E9D7FE] border-t-[#7F56D9]"></div>
                    Loading settings...
                  </div>
                )}
              </div>
            </div>
          </PageHeader>

          {/* Content */}
          <div className="">
            <div className="py-4 space-y-12">
              {/* Number Label */}
              <div className="flex items-start">
                <div className="flex-1 max-w-md">
                  <label className="block text-sm text-gray-700 font-semibold mb-2">Number Label</label>
                  <p className="text-sm text-gray-600">
                    Add a label to this number for easy recognition. Example, Campaign name or user group
                    name.
                  </p>
                </div>
                <div className="flex-1 max-w-md ml-12">
                  <Input
                    value={phoneSettings.label}
                    onChange={(e) => setPhoneSettings((prev) => ({ ...prev, label: e.target.value }))}
                    placeholder="Enter label"
                    disabled={isSaving || isDeleting || !isSettingsReady}
                    className="w-full border-gray-200 focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Number Access */}
              <div className="flex items-start">
                <div className="flex-1 max-w-md">
                  <label className="block text-sm text-gray-700 font-semibold mb-2">Number Access</label>
                  <p className="text-sm text-gray-600">
                    Assign number to a single user, set of users or accessible across the entire workspace for
                    all users with dialer access.
                  </p>
                </div>
                <div className="flex-1 max-w-md ml-12">
                  <div className="space-y-4">
                    {/* Assigned Users Option */}
                    <div
                      onClick={() => {
                        if (isSaving || isDeleting || !isSettingsReady) return;
                        if (phoneSettings.accessType !== "assigned") {
                          captureEvent("SET_PhoneNumberSettings_AccessTypeChange", {
                            phone_number_id: phoneNumberData?.id || phoneId,
                            from_access_type: phoneSettings.accessType,
                            to_access_type: "assigned",
                          });
                        }
                        setPhoneSettings((prev) => ({ ...prev, accessType: "assigned" }));
                      }}
                      className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${phoneSettings.accessType === "assigned" ? "border-2 border-[#9E77ED] bg-purple-50 dark:bg-background" : "border-gray-200 hover:border-gray-300"}`}
                    >
                      <div className="flex items-start space-x-3">
                        <input
                          type="radio"
                          id="assigned-users"
                          name="access-type"
                          value="assigned"
                          checked={phoneSettings.accessType === "assigned"}
                          onChange={(e) => {
                            if (phoneSettings.accessType !== e.target.value) {
                              captureEvent("SET_PhoneNumberSettings_AccessTypeChange", {
                                phone_number_id: phoneNumberData?.id || phoneId,
                                from_access_type: phoneSettings.accessType,
                                to_access_type: e.target.value,
                              });
                            }
                            setPhoneSettings((prev) => ({ ...prev, accessType: e.target.value }));
                          }}
                          disabled={isSaving || isDeleting || !isSettingsReady}
                          className="mt-1 h-4 w-4 text-[#9E77ED] focus:ring-[#9E77ED] disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <div className="flex-1">
                          <label htmlFor="assigned-users" className="text-sm font-medium text-gray-900">
                            Assigned users
                          </label>
                          <p className="text-xs text-gray-500 mb-3">
                            Number is accessible to all the selected users
                          </p>

                          {phoneSettings.accessType === "assigned" && (
                            <div className="mt-3">
                              <VirtualTelephonyUserSelect
                                selectedIds={phoneSettings.assignedUsers}
                                onChange={(ids) =>
                                  setPhoneSettings((prev) => ({ ...prev, assignedUsers: ids }))
                                }
                                disabled={isSaving || isDeleting || !isSettingsReady}
                                placeholder="Select users"
                                emptyMessage="No users found"
                                errorMessage="Error loading users"
                                workspaceId={activeWorkspace?.id}
                                preloadedUsers={assignedUsersFromNumber}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Accessible across workspace Option */}
                    <div
                      onClick={() => {
                        if (isSaving || isDeleting || !isSettingsReady) return;
                        if (phoneSettings.accessType !== "workspace") {
                          captureEvent("SET_PhoneNumberSettings_AccessTypeChange", {
                            phone_number_id: phoneNumberData?.id || phoneId,
                            from_access_type: phoneSettings.accessType,
                            to_access_type: "workspace",
                          });
                        }
                        setPhoneSettings((prev) => ({ ...prev, accessType: "workspace" }));
                      }}
                      className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${phoneSettings.accessType === "workspace" ? "border-2 border-[#9E77ED] bg-purple-50 dark:bg-background" : "border-gray-200 hover:border-gray-300"}`}
                    >
                      <div className="flex items-start space-x-3">
                        <input
                          type="radio"
                          id="workspace-access"
                          name="access-type"
                          value="workspace"
                          checked={phoneSettings.accessType === "workspace"}
                          onChange={(e) => {
                            if (phoneSettings.accessType !== e.target.value) {
                              captureEvent("SET_PhoneNumberSettings_AccessTypeChange", {
                                phone_number_id: phoneNumberData?.id || phoneId,
                                from_access_type: phoneSettings.accessType,
                                to_access_type: e.target.value,
                              });
                            }
                            setPhoneSettings((prev) => ({ ...prev, accessType: e.target.value }));
                          }}
                          disabled={isSaving || isDeleting || !isSettingsReady}
                          className="mt-1 h-4 w-4 text-[#9E77ED] focus:ring-[#9E77ED] disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <div className="flex-1">
                          <label htmlFor="workspace-access" className="text-sm font-medium text-gray-900">
                            Accessible across workspace
                          </label>
                          <p className="text-xs text-gray-600">
                            This number will be available to everyone in the workspace with dialer access
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Call Recordings */}
              <div className="flex items-start">
                <div className="flex-1 max-w-md">
                  <label className="block text-sm text-gray-700 font-semibold mb-2">Call recordings</label>
                  <p className="text-sm text-gray-600">
                    Enable or disable call recordings on this phone number
                  </p>
                </div>
                <div className="flex-1 max-w-md ml-12">
                  <div className="flex items-center space-x-3">
                    <Switch
                      checked={phoneSettings.recordingEnabled}
                      onCheckedChange={(checked) => {
                        captureEvent("SET_PhoneNumberSettings_RecordingToggle", {
                          phone_number_id: phoneNumberData?.id || phoneId,
                          recording_enabled: checked,
                          previous_value: phoneSettings.recordingEnabled,
                        });
                        setPhoneSettings((prev) => ({ ...prev, recordingEnabled: checked }));
                      }}
                      disabled={isSaving || isDeleting || !isSettingsReady}
                      className="data-[state=checked]:bg-[#7F56D9] data-[state=unchecked]:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <span className="text-sm font-medium text-gray-900">
                      {phoneSettings.recordingEnabled
                        ? "Call recording is enabled"
                        : "Call recording is disabled"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <GenericModal isOpen={isOpen} closeModal={closeModal}>
        <div className="flex items-start space-x-4 mb-8">
          <div className="w-12 h-12 bg-red-100 rounded-md flex items-start justify-center p-3">
            <Trash2 className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-950 leading-none">Confirm number removal</h2>
            <p className="text-sm text-gray-600 mt-1.5">
              {phoneSettings && phoneSettings.accessType === "workspace" ? (
                <>
                  This number is shared across the workspace. Removing this number will remove it for all
                  users with dialer access
                </>
              ) : (
                <>
                  Removing this number will remove it for all users with access to this number. Please review
                  before deletion.
                </>
              )}
            </p>
          </div>
        </div>
        {getAssignedUsers().length > 0 &&
          getAssignedUsers().map((user) => (
            <div className="bg-[#FAFAFA] border border-[#E9EAEB] rounded-lg p-3 mt-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-semibold text-sm">{getInitials(user?.name)}</span>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-[#181D27]">{user?.name}</h3>
                  <p className="text-xs text-[#535862] font-normal">{user?.email}</p>
                </div>
              </div>
            </div>
          ))}
        <div className="flex justify-between space-x-3  mt-6">
          <Button
            variant="outline"
            onClick={closeModal}
            className="bg-white w-4/12  text-[#414651] border border-[#D5D7DA] font-semibold px-[66px] shadow-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteNumber}
            disabled={deletePhoneNumberMutation.isLoading}
            variant="danger"
            className="flex-1"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            <span>Delete number</span>
            {deletePhoneNumberMutation.isLoading && <div className="loader_circle ml-2"></div>}
          </Button>
        </div>
      </GenericModal>
    </>
  );
}
